import { useOutletContext, Link } from "react-router-dom";
import { useState, useEffect, useRef } from 'react';
import { CopyBlock, CodeBlock, monokai } from "react-code-blocks";
import { PAGE_TITLE_BASE, PageDisplayNames, PageNames } from '../../Assets/variables';
import {
    certificateCommend,
    connectUsingJava,
    connectUsingPython,
    createCertificate,
    graphqlComposingQueries,
    graphqlFlexibleQueries,
    graphqlGenerateQuery,
    graphqlGenerateQueryUrl,
    graphqlRequestInJava,
    graphqlRequestInPython,
    graphqlUrl,
    privateKeyCommand,
    postmanOne,
    apiVersioning,
    majorVersionV2VersionUrl1,
    majorVersionV2VersionUrl2,
    majorVersionV2ErrorMessageExample,
    majorVersionV2V1Definition,
    majorVersionV2V2Definition,
    majorVersionV2V1ParameterDefinition,
    majorVersionV2V2ParameterDefinition,
    errorCodesExample
} from "../../Assets/documentationExamples";
import { htmlClientCodeExample } from "../../Assets/apiDefinitionExamples";
import Images from "../../Assets/Images/images";
import InnerNavigation from "../../Components/InnerNavigation";
import { goToSection } from "../../Utils/pageNavigationHelpers";
import { Helmet } from "react-helmet-async";

const DocumentationSections = {
    GettingStarted: "getting-started",
    Authentication: "authentication",
    RequestInPortal: "request-in-portal",
    RequestInPostman: "request-in-postman",
    HowToConnectUsingPython: "how-to-connect-using-python",
    HowToConnectUsingJava: "how-to-connect-using-java",
    WebSocketClientExample: "web-socket-client-example",
    WebSocketKeepAlive: "web-socket-keep-alive",
    GraphQLInterface: "graphql-interface",
    GraphQLRequestInJava: "graphql-request-in-java",
    GraphQLRequestInPython: "graphql-request-in-python",
    APIDataModel: "api-data-model",
    APILimits: "api-limits",
    ErrorCodes: "error-codes",
    APIVersioning: "api-versioning",
    MajorVersionUpdates: "major-version-updates",
    V2Summary: "v2-summary",
    V3Summary: "v3-summary",
    Troubleshooting: "troubleshooting"
};

function downloadPDF() {
    var hiddenElement = document.createElement('a');
    hiddenElement.href = `${process.env.PUBLIC_URL}/Certificate_Portal_User_Guide.pdf`;
    hiddenElement.target = '_blank';
    /* hiddenElement.download = 'Certificate_Portal_User_Guide.pdf'; */
    hiddenElement.rel = 'noopener noreferrer';
    hiddenElement.click();
}

const Documentation = () => {
    const {setActivePageItem, sidebarToggler} = useOutletContext();
    const [isShown, setIsShown] = useState({
        "getting-started": true,
        "graphql-interface": true,
        "api-data-model": true,
        "api-limits": true,
        "error-codes": true,
        "api-versioning": true,
        "major-version-updates": true,
        "troubleshooting": true
    });
    const gettingStartedRef = useRef(null);
    const authenticationRef = useRef(null);
    const requestInPortalRef = useRef(null);
    const requestInPostmanRef = useRef(null);
    const howToConnectUsingPythonRef = useRef(null);
    const howToConnectUsingJavaRef = useRef(null);
    const webSocketClientExampleRef = useRef(null);
    const webSocketKeepAliveRef = useRef(null);
    const graphqlInterfaceRef = useRef(null);
    const graphqlRequestInJavaRef = useRef(null);
    const graphqlRequestInPythonRef = useRef(null);
    const apiDataModelRef = useRef(null);
    const apiLimitsRef = useRef(null);
    const errorCodesRef = useRef(null);
    const apiVersioningRef = useRef(null);
    const majorVersionUpdatesRef = useRef(null);
    const V2SummaryRef = useRef(null);
    const V3SummaryRef = useRef(null);
    const troubleshootingRef = useRef(null);

    const headingRefs = {
        "getting-started": gettingStartedRef,
        "authentication": authenticationRef,
        "request-in-portal": requestInPortalRef,
        "request-in-postman": requestInPostmanRef,
        "how-to-connect-using-python": howToConnectUsingPythonRef,
        "how-to-connect-using-java": howToConnectUsingJavaRef,
        "web-socket-client-example": webSocketClientExampleRef,
        "web-socket-keep-alive": webSocketKeepAliveRef,
        "graphql-interface": graphqlInterfaceRef,
        "graphql-request-in-java": graphqlRequestInJavaRef,
        "graphql-request-in-python": graphqlRequestInPythonRef,
        "api-data-model": apiDataModelRef,
        "api-limits": apiLimitsRef,
        "error-codes": errorCodesRef,
        "api-versioning": apiVersioningRef,
        "major-version-updates": majorVersionUpdatesRef,
        "v2-summary": V2SummaryRef,
        "v3-summary": V3SummaryRef,
        "troubleshooting": troubleshootingRef
    }
    
    useEffect(() => {
        setActivePageItem(PageNames.Documentation);
        sidebarToggler(true);
        const currentUrl = window.location.href;
        const section = currentUrl.split("#")[1];
        if (section) {
            goToSection(headingRefs[section])
        }
    }, []);
    
    return (
        <>
            <Helmet>
                <title>{`${PAGE_TITLE_BASE}${PageDisplayNames.Documentation}`}</title>
                <meta name="description" content={`${PageDisplayNames.Documentation} page of the SIX Web API Portal`} />
                <link rel="canonical" href={`/${PageNames.Documentation}`} />
            </Helmet>
            <div className="documentation">
                <div className="documentation-container">
                    <div className="inner-navigation">
                        <InnerNavigation isShown={isShown} setIsShown={setIsShown} headingRefs={headingRefs} />
                    </div>
                    <div id="documentation-content">
                        <h1>Documentation</h1>
                        <h2 id={DocumentationSections.GettingStarted} ref={gettingStartedRef}>Getting Started</h2>
                        <h3 className="hidden" id={DocumentationSections.GettingStarted}>Getting Started</h3>
                        <section>
                            <h3 id={DocumentationSections.Authentication} ref={authenticationRef}>Authentication</h3>
                            <p>The SIX Web API uses MTLS (Mutual TLS) certificate authentication. You’ll need a valid certificate to access the API.</p>
                            <p>Below is the process for obtaining a valid certificate:</p>

                            <h4><strong>Step 1: Create a Certificate Signing Request (CSR)</strong></h4>
                            <p>Prerequisite: You need a SIX account before creating the CSR.</p>
                            <p>To create a CSR, follow these steps based on your operating system:</p>
                            <p><strong>UNIX / Mac:</strong> Use the commands below in your terminal:</p>

                            <p>Create a private key using the below command</p>
                            <div className="code-example">
                                <CopyBlock
                                    text={privateKeyCommand}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p>Replace <strong>full-user-identifier</strong> with your identifier (e.g., CH123-sixapiaccount), then create the CSR:</p>
                            <div className="code-example">
                                <CopyBlock
                                    text={certificateCommend}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p><strong>Windows:</strong> You need OpenSSL or Cygwin installed in order to create the CSR in Windows. The commands are the same as for UNIX / Mac.</p>
                            <p>Here is the link to download <Link to="https://www.cygwin.com/" target="_blank">Cygwin</Link></p>
                            <p>Here is the link to download <Link to="https://wiki.openssl.org/index.php/Binaries" target="_blank">OpenSSL</Link></p>

                            <h4><strong>Step 2: Sign & Register the Certificate</strong></h4>
                            <p>Once you've created your CSR, it needs to be signed. This can be done by a Certificate Authority (CA) of your choice or by SIX.</p>
                            <p>You can use the SIX portal to sign and register your certificates. If you have a signed certificate, you can register the certificates from the SIX portal.</p>

                            <ol>
                                <li>Log in to SIX portal at <Link to="https://portal.six-group.com/" target="_blank">https://portal.six-group.com</Link></li>
                                <li>Follow our <Link onClick={ () => {downloadPDF()} }>Certificate Portal User Guide</Link> to sign and register your certificates</li>
                            </ol>

                            <p>Once registered your certificate status should be active and you can download the signed-certificate in the menu as illustrated below:</p>
                            <img src={Images.Authentication1} alt="Download the signed certificate" />

                            <p>Once the file is downloaded, it is recommended to rename it to signed-certificate.pem, so the file will have the same name as the examples provided in the document. </p>

                            <h4><strong>Step 3: Request for Entitlements</strong></h4>
                            <p>Additionally, the relevant permissions should be assigned to your user account to start using the APIs.</p>
                            <p>Please contact the SIX Customer Administration team to get the required permissions assigned to your account. They will guide you through the process and ensure your account is set up correctly.</p>
                            <p>You can now use the Signed Certificate and Private Key to authenticate yourself and start using the APIs.</p>
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.RequestInPortal} ref={requestInPortalRef}>Requesting the APIs from Portal</h3>
                            <p>To access the APIs from API Portal or to run JavaScript samples in your internet browser, you need the certificate in P12 format. A P12 certificate is a binary format for storing the server certificate, any intermediate certificates, and the private key in one encryptable file. Here's how you can create one and use it:</p>

                            <h4><strong>Step 1: Create a P12 certificate</strong></h4>
                            <p>You can create a P12 certificate from the private key and the public key (signed certificate) you generated in the previous steps. Use the following command:</p>

                            <p>Command to create a P12 certificate:</p>
                            <div className="code-example">
                                <CopyBlock
                                    text={createCertificate}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p>This command will prompt you to provide a password for the certificate. Remember this password as you will need it when importing the certificate into your browser.</p>

                            <h4><strong>Step 2: Register P12 certificate in browser</strong></h4>
                            <p>The following steps are specific to the Edge browser. The same should work for Chrome as well.</p>

                            <p>Go to settings from the top menu</p>        
                            <img src={Images.Portal1} alt="Go to settings from the top menu" />

                            <p>On the settings page, select <strong>Privacy, search and services</strong> and scroll down to <strong>Security</strong>.</p>
                            <img src={Images.Portal2} alt="On the settings page, select 'Privacy, search and services' and scroll down to 'Security'." />

                            <p>Click on <strong>Manage certificates</strong> to open the certificates window.</p>
                            <img src={Images.Portal3} alt="Click on 'Manage certificates' to open the certificates window." />

                            <p>Click on <strong>Import</strong> and follow the steps to import the P12 certificate.</p>
                            <p>Note: Change the file type to <strong>Personal Information Exchange</strong> or <strong>All Files</strong> to view the P12 certificate.</p>
                            <img src={Images.Portal4} alt="Click on 'Import' and follow the steps to import the P12 certificate." />

                            <h4><strong>Step 3: Making a query</strong></h4>
                            <p>Go to API Catalog and click on an API (Instrument Base for example)</p>
                            <img src={Images.Portal5} alt="Go to API Catalog and click on an API (Instrument Base for example)" />

                            <p>Click on <strong>Swagger</strong> Tab and click on GET to expand the Swagger page</p>
                            <img src={Images.Portal6} alt="Click on 'Swagger' Tab and click on GET to expand the Swagger page" />

                            <p>Click on <strong>Try it out</strong> below to see the <strong>Execute</strong> button.</p>
                            <img src={Images.Portal7} alt="Click on 'Try' below to see the 'Execute' button. " />

                            <p>Click on Execute to Try the API</p>
                            <p>You should see a pop-up to select the certificate when you Try the first time</p>
                            <img src={Images.Portal8} alt="Click on Execute to Try the API" />

                            <p>Select the certificate you imported and click OK.</p>
                            <p>You should see the response below with Status code 200</p>
                            <img src={Images.Portal9} alt="Select the certificate you imported and click OK." />

                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.RequestInPostman} ref={requestInPostmanRef}>Requesting the APIs from Postman</h3>
                            <p>Postman provides an easy to use interface to test the APIs.</p>
                            <p><strong>Step 1</strong> - Download and install the Postman desktop agent - <Link to="https://www.postman.com/downloads/" target="_blank">Download Postman | Get Started for Free</Link></p>
                            <p><strong>Step 2</strong> - Register the certificate</p>
                            <p>Go to settings by clicking the gear icon at the top right corner</p>
                            
                            <img src={Images.Postman1} alt="Go to settings by clicking the gear icon at the top right corner" />
                    
                            <p>Click on certificates and click Add certiticate</p>
                            
                            <img src={Images.Postman2} alt="Click on certificates and click Add certiticate" />
                    
                            <p>Enter the host name as <code>api.six-group.com</code> click on Select File CRT file</p>
                            <p>Click on Select file next to CRT file and select the signed certificate</p>
                            <p>Click on Select file next KEY file and select the private key and click Add</p>
                            
                            <img src={Images.Postman3} alt="Cick on Select file next KEY file and select the private key and click Add" />
                    
                            <p><strong>Step 3</strong> - Make the request</p>
                            <p>Click on new request</p>
                    
                            <img src={Images.Postman4} alt="Click on new request" />
                    
                            <p>Add the request url next to GET</p>

                            <div className="code-example">
                                <CopyBlock
                                    text={postmanOne}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
                    
                            <img src={Images.Postman5} alt="Add the request url next to GET" />
                    
                            <p>Go to Headers and fill the API version as a header parameter</p>
                    
                            <img src={Images.Postman6} alt="Go to Headers and fill the API version as a header parameter" />
                    
                            <p>Click Send to see the response</p>
                    
                            <img src={Images.Postman7} alt="Click Send to see the response" />
                        </section>
                        <hr />
                        <section>
                            <h3 id={DocumentationSections.HowToConnectUsingPython} ref={howToConnectUsingPythonRef}>How to connect using Python</h3>

                            <div className="code-example">
                                <CopyBlock
                                    text={connectUsingPython}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                        </section>
                        <hr/>
                        <section>
                            <h3 id={DocumentationSections.HowToConnectUsingJava} ref={howToConnectUsingJavaRef}>How to connect using Java</h3>
                            
                            <div className="code-example">
                                <CopyBlock
                                    text={connectUsingJava}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                        </section>
                        <hr/>
                        <section>
                            <h3 id={DocumentationSections.WebSocketClientExample} ref={webSocketClientExampleRef}>Web socket client example</h3>
                            
                            <p>The streaming market data streams data through a web socket connection.</p>
                            <p>You need a client to receive the streaming data.</p>

                            <h4><strong>HTML Client</strong></h4>
                            <p>Below is a simple html client that you can use to receive the data.</p>

                            <div className="code-example">
                                <CopyBlock
                                    text={htmlClientCodeExample}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>        

                            <h4>Other languages</h4>
                            <p>If you are using another programming language, please check the official WebSocket library:</p>
                            <ul>
                                <li><Link to="https://pypi.org/project/websockets/" target="_blank">Python Websockets · PyPI</Link></li>
                                <li><Link to="https://docs.oracle.com/en/java/javase/11/docs/api/java.net.http/java/net/http/WebSocket.html" target="_blank">Java WebSocket (Java SE 11 & JDK 11 ) (oracle.com)</Link></li>
                            </ul>
                        </section>
                        <hr/>
                        <section>
                            <h3 id={DocumentationSections.WebSocketKeepAlive} ref={webSocketKeepAliveRef}>WebSocket Keep-Alive</h3>
                            
                            <p>To maintain a stable WebSocket connection, clients <strong>must send a ping every 10 seconds</strong>.</p>
                            <p>Failing to send regular pings can result in connection instability or disconnection. Specifically:</p>

                            <ul>
                                <li>
                                    <strong>Inactivity Timeout</strong>
                                    <ul>
                                        <li>If no messages (ping, pong, or data) are exchanged for more than <strong>one minute</strong>, the <strong>SIX Gateway</strong> will automatically <strong>close the WebSocket connection</strong>.</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Stale Connections</strong>
                                    <ul>
                                        <li>In some cases, the connection may appear active but <strong>stop receiving updates</strong>.</li>
                                        <li>This can be detected through a <strong>pong timeout</strong> — if a <strong>pong</strong> is <strong>not received within one second</strong> of sending a ping, the client should <strong>reconnect immediately</strong> to restore a stable session.</li>
                                    </ul>
                                </li>
                            </ul>

                            <h4><strong>Implementing the Ping</strong></h4>
                            <p>The SIX Web API uses <strong>standard WebSocket functionality</strong> for connection health checks. After the initial WebSocket handshake, either the <strong>client</strong> or the <strong>server</strong> may send a <strong>ping</strong> frame at any time to verify that the connection remains active. This mechanism is defined by the <strong>WebSocket protocol (RFC 6455)</strong>.</p>

                            <h4><strong>Library support</strong></h4>
                            <p>Ping/pong (keep-alive) behavior is supported by the underlying WebSocket protocol and many popular libraries:</p>
                            <ul>
                                <li>
                                    <strong>Java</strong>
                                    <ul>
                                        <li>Using the Jetty WebSocket Client, you can implement handlers for PING/PONG frames via <code>Session.Listener.onWebSocketPing(ByteBuffer)</code> and <code>onWebSocketPong(ByteBuffer)</code>. <Link to="https://jetty.org/docs/jetty/12/programming-guide/client/websocket.html" target="_blank">jetty.org</Link></li>
                                        <li>Using the Jakarta WebSocket API: you can call <code>RemoteEndpoint.sendPing(ByteBuffer)</code> and <code>sendPong(ByteBuffer)</code>. <Link to="https://tomcat.apache.org/tomcat-11.0-doc/websocketapi/index-all.html" target="_blank">tomcat.apache.org</Link></li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Python</strong>
                                    <ul>
                                        <li>The websockets library (versions 11.0+ and later) supports ping/pong as part of its keepalive mechanism. You can configure <code>ping_interval</code> and <code>ping_timeout</code>. <Link to="https://websockets.readthedocs.io/en/13.0.1/topics/keepalive.html" target="_blank">websockets</Link></li>
                                        <li>
                                            <p>Example usage:</p>
                                            <div className="code-example">
                                                <CopyBlock
                                                    text={`async with websockets.connect(uri, ping_interval=10, ping_timeout=1) as ws: # your logic`}
                                                    language={"text"}
                                                    showLineNumbers={false}
                                                    startingLineNumber={1}
                                                    theme={monokai}
                                                    codeBlock={true}
                                                />
                                            </div>
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>JavaScript / Node.js</strong>
                                    <ul>
                                        <li>The ws library (Node.js) supports <code>ws.ping()</code> and will automatically send <code>pong</code> responses to incoming <code>pings</code>. <Link to="https://www.npmjs.com/package/ws" target="_blank">npm</Link></li>
                                        <li>
                                            <p>Example snippet:</p>
                                            <div className="code-example">
                                                <CopyBlock
                                                    text={`const WebSocket = require('ws'); const client = new WebSocket(url); client.on('open', () => client.ping()); client.on('pong', () => console.log('Received pong'));`}
                                                    language={"text"}
                                                    showLineNumbers={false}
                                                    startingLineNumber={1}
                                                    theme={monokai}
                                                    codeBlock={true}
                                                />
                                            </div>
                                        </li>
                                        <li>For browsers, the native WebSocket API (via Mozilla MDN) documents the protocol detail for <code>ping</code>/<code>pong</code> frames (opcode 0x9/0xA) but notes that the browser API does <strong>not</strong> expose a method for sending <code>ping</code> frames programmatically.</li>
                                    </ul>
                                </li>
                            </ul>
                        </section>
                        <hr/>
                        <section>
                            <h2 id={DocumentationSections.GraphQLInterface} ref={graphqlInterfaceRef}>GraphQL Interface</h2>
                            <h3 className="hidden" id={DocumentationSections.GraphQLInterface}>GraphQL Interface</h3>
                            
                            <p>In addition to the REST API, SIX web API also offer a GraphQL interface that allows you to request exactly the data you need. This can make your API calls more efficient and flexible, as you can tailor your requests to your specific needs.</p>
                            <p>If you are new to GraphQL, please check the <Link to="https://graphql.org/learn/" target="_blank">GraphQL Documentation</Link> for a quick introduction</p>

                            <h4><strong>GraphQL Base URL</strong></h4>
                            <p>Below is the base URL for GraphQL API</p>

                            <div className="code-example">
                                <CopyBlock
                                    text={graphqlUrl}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p>The authentication mechanism for GraphQL is same as REST APIs and is based on MTLS certificate authentication. To receive a successful response, you need a valid certificate.</p>

                            <h4><strong>Composing Queries</strong></h4>
                            <p>In GraphQL, you compose queries by specifying the fields you want to retrieve. For example, if you want to get the <code>instrumentName</code> and <code>instrumentStatus</code> for a specific instrument, you would compose a query like this:</p>
                            
                            <div className="code-example">
                                <CopyBlock
                                    text={graphqlComposingQueries}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p>This query would return a JSON object with the <code>instrumentShortName</code> and <code>instrumentStatus</code> fields.</p>
                            <p>You can use the GraphQL Playground to compose and test your queries. (You have to install P12 certificate in your browser in order to see successful response)</p>

                            <h4><strong>Flexible Queries</strong></h4>
                            <p>One of the main advantages of GraphQL is that it allows for flexible queries. You can request multiple fields, nest fields, and even request different fields for different objects in the same query. This allows you to retrieve all the data you need in a single request, reducing the need for multiple API calls.</p>
                            <p>For example, you could extend the previous query to also retrieve the Instrument Name Prefix, Suffix and Product Name fields from the <code>Instrument Base</code>:</p>

                            <div className="code-example">
                                <CopyBlock
                                    text={graphqlFlexibleQueries}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p>This query would return a JSON object with the <code>instrumentNamePrefix</code>, <code>instrumentNameSuffix</code>, and <code>instrumentProductName</code> fields.</p>

                            <h4><strong>GraphQL Query in REST API Response</strong></h4>
                            <p>The REST API offers a unique feature that allows you to see the equivalent GraphQL query for your REST API request. This can be particularly useful if you're transitioning from using our REST API to our GraphQL API, or if you're just curious to see how your REST API request would look as a GraphQL query.</p>
                            <p>To include the equivalent GraphQL query in your REST API response, add the <code>extensions</code> parameter to your REST API request and set its value to <code>EXPLAIN</code>.</p>

                            <img src={Images.Graphql1} alt="Add the 'extensions' parameter to your REST API request and set its value to 'EXPLAIN'" />

                            <p>Below is a sample request with extensions parameter set to <code>EXPLAIN</code>:</p>

                            <div className="code-example">
                                <CopyBlock
                                    text={graphqlGenerateQueryUrl}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <p>When you include the <code>extensions=EXPLAIN</code> parameter, the REST API response will include an <code>extensions</code> field that contains the equivalent GraphQL query. Here's an example of what this might look like:</p>

                            <div className="code-example">
                                <CodeBlock
                                    text={graphqlGenerateQuery}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                        </section>
                        <hr/>
                        <section>
                            <h3 id={DocumentationSections.GraphQLRequestInJava} ref={graphqlRequestInJavaRef}>GraphQL Request in Java</h3>
                            
                            <div className="code-example">
                                <CopyBlock
                                    text={graphqlRequestInJava}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                        </section>
                        <hr/>
                        <section>
                            <h3 id={DocumentationSections.GraphQLRequestInPython} ref={graphqlRequestInPythonRef}>GraphQL Request in Python</h3>
                            
                            <div className="code-example">
                                <CopyBlock
                                    text={graphqlRequestInPython}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                        </section>
                        <hr/>
                        <section>
                            <h2 id={DocumentationSections.APIDataModel} ref={apiDataModelRef}>API Data Model</h2>
                            <h3 className="hidden" id={DocumentationSections.APIDataModel}>API Data Model</h3>
                            <h4>API Request</h4>
        
                            <p><strong>Instruments</strong></p>
                            <p>Endpoints containing <code>/instruments/</code> in the path require <strong>instrument identifiers</strong> along with an associated <strong>scheme</strong> as input parameters.</p>
                            <p>The following identifier schemes are supported:</p>
        
                            <ul>
                                <li><strong>ISIN</strong></li>
                                <li><strong>CUSIP</strong></li>
                                <li><strong>SEDOL</strong></li>
                                <li><strong>VALOR</strong></li>
                                <li><strong>CINS</strong></li>
                                <li><strong>WKN</strong></li>
                                <li><strong>COMMONCODE</strong></li>
                                <li><strong>TICKER_BC</strong></li>
                            </ul>
        
                            <h5><strong>Example:</strong></h5>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={'https://api.six-group.com/web/v2/instruments/referenceData/instrumentBase?scheme=ISIN&ids=CH0009980894'}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p><strong>Listings</strong></p>
                            <p>Endpoints containing <code>/listings/</code> in the path require <strong>instrument identifiers appended with a market identifier (Bourse Code - BC).</strong></p>
                            <p>To query a specific <strong>listing</strong>, provide the identifier in the format <code>&lt;InstrumentCode&gt;_&lt;MarketCode&gt;</code>.</p>
                            <p>Supported identifier schemes for listings:</p>
        
                            <ul>
                                <li><strong>ISIN_BC</strong></li>
                                <li><strong>CUSIP_BC</strong></li>
                                <li><strong>SEDOL_BC</strong></li>
                                <li><strong>VALOR_BC</strong></li>
                                <li><strong>CINS_BC</strong></li>
                                <li><strong>WKN_BC</strong></li>
                                <li><strong>COMMONCODE_BC</strong></li>
                                <li><strong>TICKER_BC</strong></li>
                            </ul>
        
                            <h5><strong>Example:</strong></h5>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={'https://api.six-group.com/web/v2/listings/referenceData/instrumentBase?scheme=VALOR_BC&ids=1222171_4'}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p>Note: Pricing-related endpoints are available <strong>only</strong> under <code>/listings/</code>, as market context (i.e., Bourse Code) is required to retrieve price information.</p>
        
                            <p><strong>Retrieve Market Code for a Given Instrument</strong></p>
                            <p>To retrieve a list of markets in which a given instrument is traded, use the <Link to={"/catalog/instrumentMarkets"} target="_blank"><strong>Instrument Markets</strong></Link> endpoint. This helps determine the appropriate market code (BC) to construct listing identifiers for use in listing or pricing APIs.</p>
        
                            <h4>API Response and Data Fields</h4>
        
                            <p>The list of available fields is provided in the <strong>"Content"</strong> tab on the API documentation page.</p>
                            <h5><strong>Field Availability</strong></h5>
                            <p>All fields in the API are optional and nullable. A field may be <strong>absent</strong> from the response if:</p>
        
                            <ul>
                                <li>The data is not available in the database, or</li>
                                <li>The user lacks the required entitlements.</li>
                            </ul>
        
                            <h5><strong>Entitlement Status: DATA_STATUS</strong></h5>
                            <p>To check entitlement status for each field, use the <code>DATA_STATUS</code> extension parameter.</p>
                            <p>Example API call with <code>DATA_STATUS</code> enabled:</p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={'https://api.six-group.com/web/v2/instruments/referenceData/instrumentBase?scheme=ISIN&ids=CH0009980894%2CIE00B5BMR087%2CCH0559601544%2CUS0378331005%2CJE00B1VS3770%2CUS037833AK68%2CXXX&preferredLanguage=EN&extensions=DATA_STATUS'}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <h4>Field Presence by API Type</h4>
        
                            <ul>
                                <li><strong>REST API</strong><br />
                                Unavailable fields are <strong>excluded</strong> from the response.</li>
                                <li><strong>GraphQL API</strong><br />
                                Unavailable fields are returned with a value of <code></code>null if explicitly requested.</li>
                                <li><strong>Streaming API</strong><br />
                                Unavailable fields are <strong>excluded</strong> from <code>START</code>, <code>HISTORY</code>, and <code>UPDATE</code> messages.</li>
                            </ul>
        
                            <h4>REST vs. GraphQL</h4>
        
                            <p>Both REST and GraphQL APIs return data in a consistent structure.</p>
                            <p><strong>GraphQL</strong> offers more flexibility:</p>
        
                            <ul>
                                <li>Select specific fields to reduce payload size</li>
                                <li>Fetch data from multiple endpoints in a single request</li>
                            </ul>
        
                            <p>Choose the format that best suits your integration needs.</p>
        
                        </section>
                        <hr/>
                        <section>
                            <h2 id={DocumentationSections.APILimits} ref={apiLimitsRef}>API Limits</h2>
                            <h3 className="hidden" id={DocumentationSections.APILimits}>API Limits</h3>
                            <h4>Data Volume Limits</h4>
        
                            <p>The following limits apply to the amount of data that can be requested per query:</p>
        
                            <p><strong>Request / Response APIs</strong></p>
                            <ul>
                                <li>A maximum of <strong>300 identifiers</strong> can be requested in a single query.</li>
                            </ul>

                            <p><strong>Request / Response History APIs</strong></p>
                            <ul>
                                <li><strong>1 identifier</strong> can be requested in a single query.</li>
                            </ul>

                            <p><strong>Streaming APIs</strong></p>
                            <ul>
                                <li>We recommend streaming up to <strong>4,000 listings per WebSocket connection</strong> to ensure a stable connection.</li>
                            </ul>

                            <h4>API Rate Limits</h4>
        
                            <p>The rate limits below are applied by default. If you require higher capacity, please contact customer support.</p>

                            <p><strong>Request / Response APIs</strong></p>
                            <ul>
                                <li><strong>Rate limit:</strong> 50 requests per 10 seconds</li>
                                <li>Rate-limit details are exposed via the following response headers:</li>
                                <ul>
                                    <li><code>ratelimit-limit</code>: Maximum number of requests allowed in the window</li>
                                    <li><code>ratelimit-remaining</code>: Number of requests remaining in the current window</li>
                                    <li><code>ratelimit-reset</code>: Time (in seconds) until the rate-limit window resets</li>
                                </ul>
                            </ul>

                            <p><strong>Example Response Headers</strong></p>
                            <div className="code-example">
                                <CodeBlock
                                    text={`ratelimit-limit: 50
ratelimit-remaining: 49
ratelimit-reset: 7
x-ratelimit-limit-10: 50
x-ratelimit-remaining-10: 49`}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
                            <p>Additional headers included in API responses:</p>
                            <div className="code-example">
                                <CodeBlock
                                    text={`access-control-allow-origin: *
access-control-expose-headers: *
api-version: 2024-10-01
content-encoding: zstd
content-type: application/json
date: Wed, 05 Feb 2025 14:33:03 GMT
x-correlation-id: a63236fb-60ec-4b58-8af1-e966b0e9c2b5#710154`}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>

                            <h4>Streaming API Limits</h4>
                            <ul>
                                <li>A maximum of <strong>2 WebSocket connections per user</strong> is allowed.</li>
                            </ul>

                            <p><strong>Note: </strong>Establishing a WebSocket connection <strong>counts as one request</strong> toward the standard Request / Response API rate limit (50 requests per 10 seconds). Messages sent over an established WebSocket connection (for example, <code>startStream()</code> calls) are not rate-limited.</p>
        
                        </section>
                        <hr/>
                        <section>
                            <h2 id={DocumentationSections.ErrorCodes} ref={errorCodesRef}>Error Codes</h2>
                            <h3 className="hidden" id={DocumentationSections.ErrorCodes}>Error Codes</h3>
                            
                            <p>In API <strong>version 2</strong>, error handling has been enhanced to provide more details about the error. This section explains how errors and lookup results are structured in API responses and how to interpret them.</p>
                            <p>In API version 2, each error message follows the structure below:</p>
        
                            <ul>
                                <li><strong>Category:</strong> The high-level classification of the error (e.g., <code>ENTITLEMENT_ERROR</code>).</li>
                                <li><strong>Type:</strong> A more specific subtype within the category (e.g., <code>ACCESS_DENIED</code>).</li>
                                <li><strong>Message Code:</strong> A unique numeric identifier for the error.</li>
                                <li><strong>Message:</strong> A human-readable description of the error, often including suggestions for resolution.</li>
                                <li><strong>Message Values:</strong> Additional contextual data related to the error. These can be used programmatically to handle errors more effectively.</li>
                            </ul>
        
                            <p>Example:</p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={errorCodesExample}
                                    language={"text"}
                                    showLineNumbers={true}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <h4>Response Structure</h4>
                            <p>API responses can include:</p>
        
                            <ul>
                                <li><strong>Only</strong> <code>data</code> <strong>Object</strong> (Success)</li>
                                <li><code>data</code> + <code>error</code> <strong>Object</strong> (Partial Success or Warning)</li>
                                <li><strong>Only</strong> <code>error</code> <strong>Object</strong> (Failure)</li>
                            </ul>
        
                            <h5><strong>1. Only data Object</strong></h5>
                            <p>Returned for successful requests.</p>
                            <p>Within the <code>data</code> object, each item includes a <code>lookupStatus</code> field that indicates the resolution status of the requested identifier.</p>
        
                            <p><strong>Lookup Status Values:</strong></p>
        
                            <ul>
                                <li><code>FOUND</code>: The identifier was successfully resolved; data is included.</li>
                                <li><code>NOT_FOUND</code>: The identifier is invalid or not recognized by the system.</li>
                                <li><code>NOT_AUTHORIZED</code>: The user is not entitled to access data for the identifier.</li>
                            </ul>
        
                            <blockquote>If the user lacks access permissions for a requested identifier, the <code>lookupStatus</code> will be <code>NOT_AUTHORIZED</code> and there is no additional error message.</blockquote>
        
                            <h5><strong>2. data + error Object</strong></h5>
                            <p>Returned when some, but not all, requested data could be delivered. The <code>error</code> object provides details on the issue affecting specific parts of the request.</p>
                            
                            <h5><strong>3. Only error Object</strong></h5>
                            <p>Returned when the entire request fails — for example, due to invalid parameters or missing entitlements. No <code>data</code> object is included in this case.</p>
        
                            <h4>Error Messages and Message Codes</h4>
                            <p>This section provides a list of possible error messages you may encounter when using the REST API, along with their meanings and recommended actions.</p>
                            <p>For REST API calls, HTTP status codes reflect the outcome of the request. However, for GraphQL, the HTTP status code is always <code>200</code>, and errors are returned within the response body.</p>
        
                            <p>Each error entry includes a column labeled <strong>"Returned In"</strong>, which indicates whether:</p>
                            <ul>
                                <li>The error is returned <strong>only as an error object</strong> (typically when the entire request fails), or</li>
                                <li>It is <strong>likely to include both data and error</strong> (e.g., when some parts of the request succeed while others fail).</li>
                            </ul>
                            
                            <table className="error-codes">
                                <thead>
                                    <tr>
                                        <th>HTTP Code (REST APIs)</th>
                                        <th>Category</th>
                                        <th>Type</th>
                                        <th>Message Code</th>
                                        <th>Message</th>
                                        <th>Message Values</th>
                                        <th>Returned In</th>
                                        <th>Interface</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>N/A</td>
                                        <td>CAPACITY_ERROR</td>
                                        <td>QUOTA_LIMIT_EXCEEDED</td>
                                        <td>1000</td>
                                        <td>Too many queries in the request. Allowed number of queries per websocket/subscription request: '%s'.</td>
                                        <td>allowedNumberOfQueriesPerWebsocketRequest: %s</td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>CAPACITY_ERROR</td>
                                        <td>QUOTA_LIMIT_EXCEEDED</td>
                                        <td>1001</td>
                                        <td>Number of identifiers exceeds the limit. Allowed number of identifiers per query: '%s'.</td>
                                        <td>limit: %s; requested: %s</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>CAPACITY_ERROR</td>
                                        <td>QUOTA_LIMIT_EXCEEDED</td>
                                        <td>1002</td>
                                        <td>You have reached the limit of '%s' conflation intervals. Please disable conflation on one of your streams or adjust your conflation intervals to proceed.</td>
                                        <td>limit: %s</td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>500</td>
                                        <td>DATA_FETCHING_ERROR</td>
                                        <td>INTERNAL_SERVER_ERROR</td>
                                        <td>2000</td>
                                        <td>Internal server error. Retry later. Contact SIX if problem persists.</td>
                                        <td>N/A</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>500</td>
                                        <td>DATA_FETCHING_ERROR</td>
                                        <td>REPORTING_ERROR</td>
                                        <td>2001</td>
                                        <td>Internal error. Retry later. Contact SIX if problem persists.</td>
                                        <td>N/A</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>500</td>
                                        <td>DATA_FETCHING_ERROR</td>
                                        <td>TIMEOUT</td>
                                        <td>2002</td>
                                        <td>Timed out from upstream service after '%s' ms. Reduce the query size. Contact SIX if the problem persists.</td>
                                        <td>timeout: %l</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>500</td>
                                        <td>DATA_FETCHING_ERROR</td>
                                        <td>INTERNAL_SERVER_ERROR</td>
                                        <td>2003</td>
                                        <td>Internal server error with licencing service. Retry later. Contact SIX if problem persists.</td>
                                        <td>N/A</td>
                                        <td>Data + Error</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>403</td>
                                        <td>ENTITLEMENT_ERROR</td>
                                        <td>ACCESS_DENIED</td>
                                        <td>3000</td>
                                        <td>User does not have access to the endpoint. Required function id: '%s'. Contact SIX for permissions.</td>
                                        <td>requiredFunctionId: %i</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>403</td>
                                        <td>ENTITLEMENT_ERROR</td>
                                        <td>ACCESS_DENIED</td>
                                        <td>3001</td>
                                        <td>User does not have access to the requested quality of service: '%s'. Entitled quality of service: '%s'. Contact SIX for permissions.</td>
                                        <td>
                                            requestedQualityOfService: %s
                                            <br />
                                            entitledQualityOfService: %s
                                        </td>
                                        <td>Data + Error</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>ENTITLEMENT_ERROR</td>
                                        <td>ACCESS_DENIED</td>
                                        <td>3002</td>
                                        <td>User do not have entitlement to the required quality of service. Required quality of service: '%s'.</td>
                                        <td>N/A</td>
                                        <td>Data + Error</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>403</td>
                                        <td>ENTITLEMENT_ERROR</td>
                                        <td>ACCESS_DENIED</td>
                                        <td>3003</td>
                                        <td>User does not have access to '%s'. Required function id: '%s'. Contact SIX for permissions.</td>
                                        <td>
                                            data: %s
                                            <br />
                                            requiredFunctionId: %s
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td>403</td>
                                        <td>ENTITLEMENT_ERROR</td>
                                        <td>ACCESS_DENIED</td>
                                        <td>3004</td>
                                        <td>
                                            User does not entitled to request data. Contact SIX for permissions.
                                            <br/>
                                            User does not entitled to request more than '%s'. Contact SIX for permissions.
                                        </td>
                                        <td>
                                            maxPeriod: %s
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td>403</td>
                                        <td>ENTITLEMENT_ERROR</td>
                                        <td>ACCESS_DENIED</td>
                                        <td>3005</td>
                                        <td>User is not entitled to retrieve data for the requested time range. Entitled Quality of Service: %s. Contact SIX to request the required permissions or modify the requested time range to align with the entitled Quality of Service.</td>
                                        <td>
                                            entitledQualityOfService: %s
                                        </td>
                                        <td>Data + Error</td>
                                        <td>REST, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4000</td>
                                        <td>Input parameter '%s' is invalid.</td>
                                        <td>parameter: %s</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>DUPLICATE_STREAM_ID</td>
                                        <td>4001</td>
                                        <td>Input streamId is already active. Provide a different streamId.</td>
                                        <td>requiredFunctionId: %s</td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4002</td>
                                        <td>Input '%s' cannot be null.</td>
                                        <td>parameter: %s</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4003</td>
                                        <td>Input '%s' cannot be empty.</td>
                                        <td>parameter: %s</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4004</td>
                                        <td>Reduce the length of parameter '%s'. Maximum length is '%s' characters.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            maxLength: %i
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4005</td>
                                        <td>Invalid character(s) found for name in 'invalid-name'. Allowed pattern: test.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            inputValue: %s
                                            <br />
                                            pattern: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4006</td>
                                        <td>Allowed values for '%s': '%s'.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            allowedValues: [list]
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4007</td>
                                        <td>Parameter '%s' not recognized. Check the documentation.</td>
                                        <td>parameter: %s</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4008</td>
                                        <td>No pricing fields in the query. Add at least one pricing field to the query. Check documentation for a list of available fields</td>
                                        <td>N/A</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4009</td>
                                        <td>Api major version V2 does not support requested minor version '%s'. Supported values for '%s': '%s'.</td>
                                        <td>
                                            header: %s
                                            <br />
                                            requestedMinorVersion: %s
                                            <br />
                                            supportedMinorVersions: [list]
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4010</td>
                                        <td>Requested period is invalid. Input '%s' should be before '%s'.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            shouldBeBefore: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>                                
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4011</td>
                                        <td>Minimum value for '%s': '%s'.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            minimumValue: %i
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4012</td>
                                        <td>Maximum value for '%s': '%s'.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            maximumValue: %i
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_RANGE</td>
                                        <td>4013</td>
                                        <td>Requested period is out of range. Data only available between '%s' and '%s'.</td>
                                        <td>
                                            from: %s
                                            <br />
                                            to: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>PARAMETER_NOT_READABLE</td>
                                        <td>4015</td>
                                        <td>Cannot convert parameter. Error: '%s'.</td>
                                        <td>error: %s</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>PARAMETER_REQUIRED</td>
                                        <td>4016</td>
                                        <td>Mandatory request parameter is missing: '%s'.</td>
                                        <td>missingRequestParameter: %s</td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>REQUEST_NOT_ALLOWED</td>
                                        <td>4017</td>
                                        <td>Operation not supported on WebSocket. Allowed queries for websocket: '%s'.</td>
                                        <td>allowedQueriesForWebsocket: %s</td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>NOT_SUPPORTED</td>
                                        <td>4018</td>
                                        <td>Sub-protocol not supported.</td>
                                        <td>N/A</td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>NOT_SUPPORTED</td>
                                        <td>4019</td>
                                        <td>Operation not supported on this sub-protocol.</td>
                                        <td>N/A</td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>200</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>NOT_SUPPORTED</td>
                                        <td>4020</td>
                                        <td>Selected BC cannot deliver '%s' quality of service. Please try with a different quality of service.</td>
                                        <td>requestedQualityOfService</td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4021</td>
                                        <td>Input '%s' at index '%s' cannot be empty.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            index: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>PARAMETER_NOT_READABLE</td>
                                        <td>4022</td>
                                        <td>Cannot convert parameter '%s' at index '%s' with value '%s'. Error: '%s'.</td>
                                        <td>
                                            parameter: %s
                                            <br />
                                            index: %s
                                            <br />
                                            error: %s
                                            <br />
                                            value: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4023</td>
                                        <td>The header '%s' is invalid. Valid format: '%s'.</td>
                                        <td>
                                            header: %s
                                            <br />
                                            validFormat: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>GRAPHQL_ERROR</td>
                                        <td>4024</td>
                                        <td>%s</td>
                                        <td>the graphql error see: <a href="https://graphql.org/learn/validation/" target="_blank">https://graphql.org/learn/validation/</a></td>
                                        <td>Error Only</td>
                                        <td>REST, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>NOT_SUPPORTED</td>
                                        <td>4025</td>
                                        <td>Streaming is disabled on this environment.</td>
                                        <td></td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>NOT_SUPPORTED</td>
                                        <td>4027</td>
                                        <td>Duplicated id is not allowed. Duplicated id: '%s'. Original id: '%s'.</td>
                                        <td>
                                            duplicatedId: %s,
                                            <br />
                                            originalId: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4028</td>
                                        <td>Increase the length of parameter '%s'. Minimum length is '%s' characters.</td>
                                        <td>
                                            parameter: %s,
                                            <br />
                                            minLength: %s
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td>400</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4029</td>
                                        <td>Invalid input for Base64 encoding or decoding for parameter '%s' with value: '%s'. Underlying: '%s'.</td>
                                        <td>
                                            parameter: %s,
                                            <br />
                                            input: %s,
                                            <br />
                                            underlying: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4030</td>
                                        <td>Wrong conflation type for requested conflation period. Please set the conflationType to one of these values: '%s'.</td>
                                        <td>validConflationTypes: %s</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4031</td>
                                        <td>Conflation period is missing for requested conflation type '%s'.</td>
                                        <td>requestedConflationType: %s</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4032</td>
                                        <td>Stream '%s' does not exist or has already been stopped.</td>
                                        <td>
                                            parameter: streamId,
                                            <br />
                                            inputValue: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4033</td>
                                        <td>Invalid value '%s' for field '%s'.</td>
                                        <td>
                                            parameter: %s,
                                            <br />
                                            inputValue: %s,
                                            <br />
                                            allowedValues: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4034</td>
                                        <td>
                                            Parameter 'ids' must contain single id or be a list. Example:
                                            <br />
                                            ids: \"111_11\" or ids: [\"111_11\", \"111_11\"]
                                        </td>
                                        <td>
                                            parameter: "ids",
                                            <br />
                                            "inputValue": %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>REST, Streaming, GraphQL</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>NOT_FOUND</td>
                                        <td>NOT_FOUND</td>
                                        <td>4035</td>
                                        <td>Listing not found for id:%s, schema:%s</td>
                                        <td>
                                            listingId: %s,
                                            <br />
                                            listingScheme: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>N/A</td>
                                        <td>VALIDATION_ERROR</td>
                                        <td>INVALID_INPUT</td>
                                        <td>4036</td>
                                        <td>Duplicate value '%s' for field '%s' is not allowed.</td>
                                        <td>
                                            parameter: %s,
                                            <br />
                                            inputValue: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td>500</td>
                                        <td>DATA_FETCHING_ERROR</td>
                                        <td>NOT_FOUND</td>
                                        <td>4035</td>
                                        <td>Listing not found for id:%s, schema:%s</td>
                                        <td>
                                            listingId: %s,
                                            <br />
                                            listingScheme: %s
                                        </td>
                                        <td>Error Only</td>
                                        <td>Streaming</td>
                                    </tr>
                                    <tr>
                                        <td></td>
                                        <td>OTHER</td>
                                        <td>OTHER</td>
                                        <td>9999</td>
                                        <td>Unknown error. Contact SIX to report this error.</td>
                                        <td>N/A</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>
                        <hr/>
                        <section>
                            <h2 id={DocumentationSections.Troubleshooting} ref={troubleshootingRef}>Troubleshooting</h2>
                            <h3 className="hidden" id={DocumentationSections.Troubleshooting}>Troubleshooting</h3>

                            <h4><strong>Authentication</strong></h4>
                            <p>If you encounter authentication issues related to client certificates, refer to the possible causes and resolutions below:</p>

                            <table className="troubleshooting">
                                <thead>
                                    <tr>
                                        <th>HTTP Code</th>
                                        <th>Message</th>
                                        <th>Possible Reason</th>
                                        <th>Recommended Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>401</td>
                                        <td><code>{`{"message":"No required TLS certificate was sent"}`}</code></td>
                                        <td>The client certificate is missing or not being sent by the browser or API client.</td>
                                        <td>
                                            <ul>
                                                <li>Verify that the certificate and private key are correctly configured and located.</li>
                                                <li>If testing from a browser, ensure that your <code>.p12</code> certificate is properly imported and selected.</li>
                                                <li>Confirm that your API client is configured to use mutual TLS (mTLS).</li>
                                            </ul>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>401</td>
                                        <td><code>{`{"message":"TLS certificate failed verification"}`}</code></td>
                                        <td>The client certificate cannot be verified — the root or intermediate certificate is missing, or the issuing CA is not trusted by the API Gateway</td>
                                        <td>
                                            <ul>
                                                <li>Ensure the full chain certificate (including intermediate and root certificates) is sent with the request.</li>
                                                <li>If using a custom or non-SIX Certificate Authority, contact SIX Support for assistance.</li>
                                            </ul>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>403</td>
                                        <td><code>{`{ "message":"Forbidden" }`}</code></td>
                                        <td>The client certificate is not registered with SIX.</td>
                                        <td>
                                            <ul>
                                                <li>Register your certificate in the certificate portal (<Link onClick={ () => {downloadPDF()} }>Certificate Portal User Guide</Link>)</li>
                                                <li>If you are using a trial account, contact SIX Support to have your certificate registered.</li>
                                            </ul>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <h4><strong>Stale Web Socket Connections</strong></h4>
                            <p>If your Web Socket is connected but not receiving updates, it might be due to a stale connection. Please implement the ping mechanism as described in <Link onClick={ () => {goToSection(webSocketKeepAliveRef)} }>WebSocket Keep-Alive</Link> section.</p>
                        </section>
                        <hr/>
                        <section>
                            <h2 id={DocumentationSections.APIVersioning} ref={apiVersioningRef}>API Versioning</h2>
                            <h3 className="hidden" id={DocumentationSections.APIVersioning}>API Versioning</h3>
        
                            <p>Web API's versioning is designed to ensure backward compatibility and to inform clients of new features and any changes that could affect usage of the API. This involves a combination of major and minor versions to distinguish between types of changes.</p>
                            <strong>Example API Request</strong>
                            <p>The major version number as part of the URL path and the minor version is communicated through the 'api-version' HTTP header in the format of a date. Below is the example query with minor and major versions. </p>
                            
                            <div className="code-example">
                                <CodeBlock
                                    text={apiVersioning}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <h4><strong>Major Version</strong></h4>
                            <ul>
                                <li>The major version is incremented with breaking changes to the API's query structure, such as changes to the request path or query parameters that are incompatible with previous versions.</li>
                                <li>It is mandatory to include the major version in the API endpoint URL.</li>
                            </ul>
                            <p><strong>Support Policy:</strong> When a new major version is released, the previous version will be supported for <strong>two more years</strong>.</p>
        
                            <h4><strong>Minor Version</strong></h4>
                            <ul>
                                <li>The minor version is updated in response to changes that could affect the API's response such as the addition or deprecation of fields and enumerations.</li>
                                <li>Specifying a minor version is optional but recommended for ensuring consistent API behavior across updates.</li>
                                <li>If a minor version is not specified by the client, the API will default to the most recent minor version available for the specified major version.</li>
                            </ul>
                            <p><strong>Support Policy:</strong> All minor versions under a major version are supported as long as the major version is supported.</p>
        
                            <h4><strong>Recommended Best Practices</strong></h4>
                            <ul>
                                <li><strong>Minor Version Pinning:</strong> Clients are encouraged to specify a minor version in their API requests to ensure consistent behavior. This practice also aids in managing changes and planning for upgrades.</li>
                                <li><strong>Fallback Strategy:</strong> For enumeration values, clients should implement a fallback mechanism, labeled "OTHER," to gracefully handle any new values introduced in future versions.</li>
                                <li><strong>Monitoring and Upgrading:</strong> The <Link to={"/release-notes"} target="_blank">release notes</Link> and version history pages for each API will help you to follow the updates to each version</li>
                            </ul>
                        </section>
                        <hr/>
                        <section>
                            <h2 id={DocumentationSections.MajorVersionUpdates} ref={majorVersionUpdatesRef}>Major Version Updates</h2>
                            <h3 className="hidden" id={DocumentationSections.MajorVersionUpdates}>Major Version Updates</h3>
                            <h3 id={DocumentationSections.V2Summary} ref={V2SummaryRef}>V2 Release Summary</h3>
                            <h4><strong>Version URL</strong></h4>
                            <p>To use the new version, update your URL from:</p>
                            
                            <div className="code-example">
                                <CopyBlock
                                    text={majorVersionV2VersionUrl1}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
                            <p>to:</p>
                            <div className="code-example">
                                <CopyBlock
                                    text={majorVersionV2VersionUrl2}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <h4><strong>1. Updated Error Messages</strong></h4>
                            <p><strong>Impacted APIs: All</strong></p>
                            <ul>
                                <li>Error messages now provide clearer details to help clients identify issues quickly.</li>
                                <li><strong>Error Message Structure: </strong>Each error message includes the following fields:</li>
                                <ul>
                                    <li><strong>Category: </strong>The general group of the error.</li>
                                    <li><strong>Type: </strong>A further classification of the error.</li>
                                    <li><strong>Message Code: </strong>A unique numerical identifier for the error.</li>
                                    <li><strong>Message: </strong>A description of the error along with possible solutions.</li>
                                    <li><strong>Message Values: </strong>Additional values that can be used in your code if needed.</li>
                                </ul>
                            </ul>
                            <p><strong>Example Error Message:</strong></p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={majorVersionV2ErrorMessageExample}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p>List of all error types can be found at <Link onClick={ () => {goToSection(errorCodesRef)} }>Error Codes</Link></p>
        
                            <h4><strong>2. Change CloseStream query</strong></h4>
                            <p><strong>Impacted APIs: Streaming Market Data, Streaming Intraday Trades with History</strong></p>
                            <p>The <code>CloseStream</code> type has been updated to remove deprecated fields.</p>
                            <p><strong>v1 Definition:</strong></p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={majorVersionV2V1Definition}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p><strong>v2 Definition:</strong></p>
        
                            <div className="code-example">
                                <CodeBlock
                                    text={majorVersionV2V2Definition}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p><strong>Impact:</strong></p>
                            <ul>
                                <li>The <code>requestedId</code> and <code>requestedScheme</code> fields have been removed.</li>
                                <li>If your application relies on these fields, update your implementation accordingly.</li>
                            </ul>
        
                            <h4><strong>3. Change to Identifier Handling in History Endpoints</strong></h4>
                            <p><strong>Impacted APIs:</strong></p>
                            <ul>
                                <li>End of Day History</li>
                                <li>Intraday History</li>
                            </ul>
                            <p>In <strong>v2</strong>, these endpoints now support <strong>only one identifier per request.</strong></p>
                            <p><strong>Reason for the Change:</strong></p>
                            <p>To improve reliability and prevent timeouts when requesting large datasets, the number of identifiers per request has been limited to one. This ensures faster and more consistent response times.</p>
        
                            <h4><strong>4. Enhanced Price Adjustment Options in <code>endOfDayHistory</code> Endpoint</strong></h4>
                            <p><strong>Impacted APIs: End of Day History</strong></p>
                            <p>In API v2, the <code>endOfDayHistory</code> endpoint's <code>priceAdjustment</code> parameter has been expanded to offer more granular control over price adjustments related to corporate actions and dividends.</p>
        
                            <p><strong>v1 Parameter Definition:</strong></p>
                            <div className="code-example">
                                <CodeBlock
                                    text={majorVersionV2V1ParameterDefinition}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p><strong>v2 Parameter Definition:</strong></p>
                            <div className="code-example">
                                <CodeBlock
                                    text={majorVersionV2V2ParameterDefinition}
                                    language={"text"}
                                    showLineNumbers={false}
                                    startingLineNumber={1}
                                    theme={monokai}
                                    codeBlock={true}
                                />
                            </div>
        
                            <p><strong>Key Changes:</strong></p>
                            <ul>
                                <li><strong>New Adjustment Options: </strong>Introduced <code>DIVIDEND_ADJUSTED_FORWARD</code> and <code>DIVIDEND_ADJUSTED_BACKWARD</code> to provide flexibility in handling dividend adjustments:</li>
                                <ul>
                                    <li><code>DIVIDEND_ADJUSTED_FORWARD</code>: Adjusts prices forward for dividends, reflecting the impact of dividends on future prices.</li>
                                    <li><code>DIVIDEND_ADJUSTED_BACKWARD</code>: Adjusts prices backward for dividends, modifying historical prices to account for dividend payouts.</li>
                                </ul>
                                <li><strong>Updated Descriptions: </strong>Clarified parameter descriptions to specify the nature of price adjustments, including distinctions between corporate actions and dividend adjustments</li>
                            </ul>
        
                            <h4><strong>5. Schema Correction for Nullable Fields</strong></h4>
                            <p><strong>Impacted APIs: All</strong></p>
                            <p>In <strong>version 1</strong>, the API schema incorrectly marked several fields as <strong>mandatory (non-nullable)</strong>, even though these fields can occasionally return <code>null</code> when data is unavailable.</p>
                            <p>Starting with <strong>version 2</strong>, the schema has been corrected to properly reflect these fields as <strong>nullable</strong> where appropriate. This ensures better alignment with actual data behavior and prevents unexpected validation errors. Additionally, improved error messaging helps clarify when and why a field might be null.</p>
        
                            <p><strong>Corrected Fields (Now Nullable in v2):</strong></p>
                            <ul>
                                <li><code>listingShortName</code></li>
                                <li><code>marketShortName</code></li>
                                <li><code>listingCurrency</code></li>
                                <li><code>listingStatus</code></li>
                                <li><code>valor</code></li>
                                <li><code>shortName</code></li>
                                <li><code>bc</code></li>
                                <li><code>longName</code></li>
                                <li><code>timestamp</code></li>
                                <li><code>unixTimestamp</code></li>
                                <li><code>message</code></li>
                                <li><code>lookupStatus</code></li>
                                <li><code>instrumentShortName</code></li>
                                <li><code>instrumentType</code></li>
                                <li><code>instrumentStatus</code></li>
                                <li><code>instrumentNamePrefix</code></li>
                                <li><code>gk</code></li>
                                <li><code>securityType</code></li>
                                <li><code>entityShortName</code></li>
                                <li><code>entityType</code></li>
                                <li><code>entityStatus</code></li>
                                <li><code>marketStatus</code></li>
                                <li><code>marketType</code></li>
                                <li><code>sessionDate</code></li>
                                <li><code>qualityOfService</code></li>
                                <li><code>requestedText</code></li>
                                <li><code>matchingDescription</code></li>
                                <li><code>normalizedScore</code></li>
                                <li><code>listings</code></li>
                            </ul>
                            <p>If your application assumes these fields are always present, you must update your logic to handle null values gracefully. Refer to the updated schema in v2 documentation for proper field handling.</p>
        
                            <h4><strong>6. Removed Fields</strong></h4>
                            <p><strong>The fields that were deprecated in v1 are deleted in v2</strong></p>
                        </section>
                        <section>
                            <h3 id={DocumentationSections.V3Summary} ref={V3SummaryRef}>V3 Release Summary</h3>
                            <p>Web API Major Version V3 will be released by the end of 2026. Currently <Link to={"/catalog/streamingMarketDataV3/overview"} target="_blank">Market Data Streaming</Link> is available in V3 in beta.</p>
                            <p>Please refer to the API definition tab to see the new API request and response structures.</p>
                            <p>The page will be updated once V3 is released.</p>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Documentation;